import _ from 'lodash'
import Backbone from 'backbone'
import AdminTwoColumnEditor from 'scoreboard/admin/admin-two-column-editor'
import DatasetsTree from './rollup-tree'
import DatasetsTreeEditorSelector from './rollup-tree-editor-selector'
import DatasetsTreeEditorRightColumn from './rollup-tree-editor-right-column'
import { CHANGE_ITEM_EVENT } from '../admin-two-column-editor'

/**
 * @extends AdminTwoColumnEditor
 */
var AdminRollupTrees = AdminTwoColumnEditor.extend(/** @lends AdminRollupTrees.prototype */{

	urlRoot: 'admin/dataset-trees',
	Model: DatasetsTree,
	SelectorView: DatasetsTreeEditorSelector,
	DisplayView: DatasetsTreeEditorRightColumn,
	heading: 'datasets.dataType.rollupTrees',

	className: AdminTwoColumnEditor.prototype.className + ' admin-datasets-trees',

	// Options passed from changeItem to url: 'admin/dataset-trees/{id}'
	loadItemOptions: {
		// return the rollupTreeGroups with the principals for this tree
		rollupTreeGroups: true
	},

	initialize: function(options = {}) {
		AdminTwoColumnEditor.prototype.initialize.apply(this, arguments)
		const {rollupTreeGroups} = this.options
		this.treeId = this.options.itemId
		this.rollupTreeGroupsById = _.keyBy(rollupTreeGroups, 'id')
		// #58633 listen for 'copy:rollupTreeGroup' so we can update the
		// associated items when a rollup tree group is copied
		this.listenTo(Backbone, 'copy:rollupTreeGroup', this._copyItem)
	},

	render: function() {
		AdminTwoColumnEditor.prototype.render.apply(this, arguments)
		this.selectorView.on(CHANGE_ITEM_EVENT, tree => {
			this.treeId = tree.id
		})
		this.$('.copy, .delete').addClass('is-not-displayed')
		// The selector view is no longer showing a tree because all the items
		// were deleted. Lets get rid of the display view until there's something
		// to actually display.
		this.selectorView.on('remove:gardener', () => _.invoke(this.displayView, 'remove'))

		this.on('error:post-formview', (model,xhr) => {
			// ES5 doesn't jive with 'arguments'
			const {arguments: args, messageCode} = xhr.responseJSON
			if ('datasets.trees.keys.duplicate' === messageCode) {
				this.displayView.showKeyErrors(messageCode, args)
			}
		})
	},

	requiresNavOnChangedItem: function(itemId) {
		if (_.isNil(this.item)) {
			// Don't nav and wipe out the /rollupTreeGroup/id if that was requested
			return _.isNil(_.get(this.options, 'rollupTreeGroupId'))
		}
		return itemId != this.item.id
	},

	/**
	 * We have to merge the selected tree node in the left column gardener
	 * with the rollupTreeGroups/keysets in the right column, so we just fully
	 * override
	 * this function and do it here.
	 *
	 * @return {DatasetTree} will be POSTed to the server when the form is
	 *     submitted
	 */
	gatherData: function() {
		// rollupTreeGroups, keysets
		const treeData = this.displayView?.readFromDom() ?? {}
		return _.extend({id: this.treeId}, treeData)
	},

	handleError: function(model, xhr) {
		// don't edit the xhr directly, since it may be used by other event handlers
		const xhrForHandler = _.cloneDeep(xhr)
		const responseJson = _.get(xhrForHandler, 'responseJSON')
		/** #46124 if this is a key set error, we need to go through the regular formView
		 * error handling process to show the banner, but it will choke on the arguments
		 * given from the server {@link DuplicateKeyException.kt}, so get rid of them
		 * here and they will displayed correctly by {@link admin-datasets-trees.js}
		 * */
		if (!_.isEmpty(responseJson) &&
			responseJson.messageCode === 'datasets.trees.keys.duplicate') {
			responseJson.arguments = []
		}

		AdminTwoColumnEditor.prototype.handleError.apply(this, [model, xhrForHandler])
	},

	/**
	 * callback after an item has been added
	 */
	_addItem: function(item) {
		AdminTwoColumnEditor.prototype._addItem.apply(this, arguments)
		this._updateAssociatedItems()
	},

	/**
	 * callback after an item has been deleted
	 */
	_deleteItem: function(item) {
		AdminTwoColumnEditor.prototype._deleteItem.apply(this, arguments)
		this._updateAssociatedItems()
	},

	/**
	 * After an item has been copied, call _updateAssociatedItems to update the
	 * rollup tree groups and keySets on the tree (as we already do when an item
	 * is added or deleted).
	 * We need to make sure the rollup tree groups are updated on this.originalItem,
	 * so that cancelling the form after some other change will not cause a
	 * newly copied rollup tree group to be lost (#58633)
	 */
	_copyItem: function() {
		this._updateAssociatedItems()
	},

	/**
	 * updates the rollupTreeGroups and keySets on the item. adding or deleting a
	 * rollupTreeGroup/keySet automatically saves to the server without hitting the
	 * main "save" button on the page so the item needs to be updated to account
	 * for this
	 */
	_updateAssociatedItems: function() {
		const { rollupTreeGroups, keySets } = this.gatherData()
		this.item?.set({ rollupTreeGroups, keySets })
		this.originalItem = this.item?.cloneDeep()
	}

})

export default AdminRollupTrees

AdminRollupTrees.__filename__ = 'admin/datasets/admin-rollup-trees.js'
